import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Truck, Loader2, Download, MapPin, Phone, User, Package, Check, Clock, AlertTriangle, FileText, Camera, PenTool } from "lucide-react";
import { toast } from "sonner";

const statusColors = {
  Draft: "bg-gray-100 text-gray-600 border-gray-200",
  Assigned: "bg-blue-50 text-blue-700 border-blue-200",
  "In Transit": "bg-amber-50 text-amber-700 border-amber-200",
  Delivered: "bg-green-50 text-green-700 border-green-200",
  Failed: "bg-red-50 text-red-700 border-red-200",
};
const fmt = (v) => `R${(parseFloat(v) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;

export default function DeliveryNotesPage() {
  const { id } = useParams();
  if (id === "new") return <CreateDeliveryNote />;
  if (id) return <DeliveryNoteDetail dnId={id} />;
  return <DeliveryNoteList />;
}

function DeliveryNoteList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/delivery-notes?status=${filter}&search=${search}&limit=50`)
      .then(r => { setItems(r.data.delivery_notes); setTotal(r.data.total); setStats(r.data.stats || {}); })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, [filter, search]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="delivery-notes-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-[#1F2937]">Delivery Notes</h1>
          <p className="text-sm text-gray-500 mt-1">{total} delivery note{total !== 1 ? "s" : ""}</p>
        </div>
        <Button data-testid="create-delivery-note-btn" onClick={() => navigate("/delivery-notes/new")} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
          <Plus className="w-4 h-4 mr-1.5" />New Delivery Note
        </Button>
      </div>
      {/* Stats */}
      <div className="grid grid-cols-5 gap-3 mb-5">
        {[["Draft", "draft", "text-gray-600 bg-gray-50"], ["Assigned", "assigned", "text-blue-600 bg-blue-50"], ["In Transit", "in_transit", "text-amber-600 bg-amber-50"], ["Delivered", "delivered", "text-green-600 bg-green-50"], ["Failed", "failed", "text-red-600 bg-red-50"]].map(([label, key, cls]) => (
          <div key={key} className={`p-3 rounded-sm border ${cls}`}>
            <div className="text-lg font-bold">{stats[key] || 0}</div>
            <div className="text-xs font-medium">{label}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mb-4">
        <Input data-testid="dn-search-input" placeholder="Search delivery notes..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs rounded-sm" />
        {["", "Draft", "Assigned", "In Transit", "Delivered", "Failed"].map(s => (
          <Button key={s} size="sm" variant={filter === s ? "default" : "outline"} onClick={() => setFilter(s)} className={`rounded-sm text-xs ${filter === s ? "bg-[#E63946] text-white" : ""}`}>{s || "All"}</Button>
        ))}
      </div>
      {loading ? <div className="animate-pulse h-40 bg-gray-100 rounded-sm" /> : items.length === 0 ? (
        <Card className="border rounded-sm"><CardContent className="p-12 text-center text-gray-400"><Truck className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No delivery notes found</p></CardContent></Card>
      ) : (
        <Card className="border rounded-sm shadow-sm"><div className="overflow-x-auto">
          <table data-testid="delivery-notes-table" className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50/50">
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Number</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Date</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Customer</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Source</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Driver</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Items</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
            </tr></thead>
            <tbody>{items.map(dn => (
              <tr key={dn.dn_id} data-testid={`dn-row-${dn.dn_id}`} className="border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer" onClick={() => navigate(`/delivery-notes/${dn.dn_id}`)}>
                <td className="p-3 font-medium text-[#E63946]">{dn.delivery_note_number}</td>
                <td className="p-3 text-gray-600">{dn.delivery_date || formatDate(dn.created_at)}</td>
                <td className="p-3 text-gray-700">{dn.customer_name}</td>
                <td className="p-3"><Badge className="rounded-full text-xs bg-gray-100 text-gray-600">{dn.source_type} {dn.source_number}</Badge></td>
                <td className="p-3 text-gray-600">{dn.driver_name || "-"}</td>
                <td className="p-3 text-gray-600">{(dn.line_items || []).length}</td>
                <td className="p-3"><Badge className={`rounded-full text-xs border ${statusColors[dn.status] || "bg-gray-100"}`}>{dn.status}</Badge></td>
              </tr>
            ))}</tbody>
          </table>
        </div></Card>
      )}
    </div>
  );
}

function CreateDeliveryNote() {
  const navigate = useNavigate();
  const [sourceType, setSourceType] = useState("invoice");
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState("");
  const [form, setForm] = useState({ delivery_address: "", delivery_date: new Date().toISOString().split("T")[0], driver_name: "", driver_phone: "", driver_vehicle: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const endpoint = sourceType === "invoice" ? "/invoices?limit=200" : sourceType === "quote" ? "/quotes?limit=200" : "/purchase-orders?limit=200";
    api.get(endpoint).then(r => {
      const list = r.data.invoices || r.data.quotes || r.data.purchase_orders || [];
      setSources(list);
    }).catch(() => setSources([]));
    setSelectedSource("");
  }, [sourceType]);

  const getSourceId = (src) => src.invoice_id || src.quote_id || src.po_id;
  const getSourceNum = (src) => src.invoice_number || src.quote_number || src.po_number;

  const save = async () => {
    if (!selectedSource) return toast.error("Please select a source document");
    setSaving(true);
    try {
      const res = await api.post("/delivery-notes", { source_type: sourceType, source_id: selectedSource, ...form });
      toast.success("Delivery note created");
      navigate(`/delivery-notes/${res.data.dn_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="create-delivery-note">
      <Button variant="ghost" size="sm" onClick={() => navigate("/delivery-notes")} className="mb-4"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
      <h1 className="font-heading font-bold text-2xl text-[#1F2937] mb-6">New Delivery Note</h1>
      <Card className="border rounded-sm"><CardContent className="p-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Source Type</label>
            <Select value={sourceType} onValueChange={setSourceType}>
              <SelectTrigger data-testid="source-type-select" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="invoice">Invoice</SelectItem>
                <SelectItem value="quote">Quote</SelectItem>
                <SelectItem value="purchase_order">Purchase Order</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Source Document</label>
            <Select value={selectedSource} onValueChange={setSelectedSource}>
              <SelectTrigger data-testid="source-doc-select" className="rounded-sm"><SelectValue placeholder="Select document..." /></SelectTrigger>
              <SelectContent>{sources.map(s => (
                <SelectItem key={getSourceId(s)} value={getSourceId(s)}>{getSourceNum(s)} - {s.customer_name}</SelectItem>
              ))}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Delivery Address</label>
            <Input data-testid="delivery-address-input" value={form.delivery_address} onChange={(e) => setForm({...form, delivery_address: e.target.value})} className="rounded-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Delivery Date</label>
            <Input type="date" data-testid="delivery-date-input" value={form.delivery_date} onChange={(e) => setForm({...form, delivery_date: e.target.value})} className="rounded-sm" />
          </div>
        </div>
        <div className="border-t pt-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Driver Details (Optional)</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Driver Name</label>
              <Input data-testid="driver-name-input" value={form.driver_name} onChange={(e) => setForm({...form, driver_name: e.target.value})} className="rounded-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Phone</label>
              <Input data-testid="driver-phone-input" value={form.driver_phone} onChange={(e) => setForm({...form, driver_phone: e.target.value})} className="rounded-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Vehicle</label>
              <Input data-testid="driver-vehicle-input" value={form.driver_vehicle} onChange={(e) => setForm({...form, driver_vehicle: e.target.value})} className="rounded-sm" />
            </div>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Notes</label>
          <textarea data-testid="dn-notes-input" className="w-full rounded-sm border p-2.5 text-sm" rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} />
        </div>
        <div className="flex gap-2 pt-2">
          <Button data-testid="save-delivery-note-btn" onClick={save} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Create Delivery Note
          </Button>
          <Button variant="outline" onClick={() => navigate("/delivery-notes")} className="rounded-sm">Cancel</Button>
        </div>
      </CardContent></Card>
    </div>
  );
}

function DeliveryNoteDetail({ dnId }) {
  const navigate = useNavigate();
  const [dn, setDn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [driverDialog, setDriverDialog] = useState(false);
  const [driverForm, setDriverForm] = useState({ driver_name: "", driver_phone: "", driver_vehicle: "" });
  const [podDialog, setPodDialog] = useState(false);
  const [podForm, setPodForm] = useState({ received_by: "", signature_base64: "" });
  const [statusDialog, setStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [actioning, setActioning] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/delivery-notes/${dnId}`)
      .then(r => { setDn(r.data); setDriverForm({ driver_name: r.data.driver_name || "", driver_phone: r.data.driver_phone || "", driver_vehicle: r.data.driver_vehicle || "" }); })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, [dnId]);
  useEffect(() => { load(); }, [load]);

  const assignDriver = async () => {
    setActioning(true);
    try {
      await api.post(`/delivery-notes/${dnId}/assign-driver`, driverForm);
      toast.success("Driver assigned"); setDriverDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActioning(false); }
  };

  const changeStatus = async () => {
    setActioning(true);
    try {
      await api.post(`/delivery-notes/${dnId}/update-status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`); setStatusDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActioning(false); }
  };

  const submitPod = async () => {
    setActioning(true);
    try {
      await api.post(`/delivery-notes/${dnId}/pod-data`, podForm);
      toast.success("Proof of delivery captured"); setPodDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActioning(false); }
  };

  const downloadPdf = () => {
    api.get(`/delivery-notes/${dnId}/pdf`, { responseType: "blob" }).then(r => {
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `delivery-note-${dn?.delivery_note_number || ""}.pdf`; a.click();
    }).catch(() => toast.error("Failed to download PDF"));
  };

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!dn) return <div className="p-8 text-center text-gray-400">Not found</div>;

  const validTransitions = { Draft: ["Assigned", "In Transit"], Assigned: ["In Transit", "Draft"], "In Transit": ["Delivered", "Failed"], Failed: ["In Transit", "Draft"] };
  const allowed = validTransitions[dn.status] || [];
  const pod = dn.proof_of_delivery;

  return (
    <div data-testid="delivery-note-detail">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/delivery-notes")}><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <h1 className="font-heading font-bold text-2xl text-[#1F2937]">{dn.delivery_note_number}</h1>
          <p className="text-sm text-gray-500">From {dn.source_type} {dn.source_number}</p>
        </div>
        <Badge className={`rounded-full text-sm px-3 py-1 border ${statusColors[dn.status]}`}>{dn.status}</Badge>
      </div>

      {/* Actions */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {dn.status !== "Delivered" && <Button data-testid="assign-driver-btn" size="sm" variant="outline" className="rounded-sm" onClick={() => setDriverDialog(true)}><User className="w-4 h-4 mr-1" />Assign Driver</Button>}
        {allowed.length > 0 && <Button data-testid="change-status-btn" size="sm" variant="outline" className="rounded-sm" onClick={() => setStatusDialog(true)}><Clock className="w-4 h-4 mr-1" />Change Status</Button>}
        {(dn.status === "In Transit" || dn.status === "Assigned") && <Button data-testid="capture-pod-btn" size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-sm" onClick={() => setPodDialog(true)}><PenTool className="w-4 h-4 mr-1" />Capture POD</Button>}
        <Button data-testid="download-dn-pdf-btn" size="sm" variant="outline" className="rounded-sm" onClick={downloadPdf}><Download className="w-4 h-4 mr-1" />PDF</Button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="border rounded-sm"><CardContent className="p-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />Delivery Info</h3>
          <div className="space-y-2 text-sm">
            <div><span className="text-gray-500">Customer:</span> <span className="font-medium">{dn.customer_name}</span></div>
            <div><span className="text-gray-500">Address:</span> <span>{dn.delivery_address || "-"}</span></div>
            <div><span className="text-gray-500">Date:</span> <span>{dn.delivery_date || "-"}</span></div>
          </div>
        </CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3 flex items-center gap-1"><Truck className="w-3.5 h-3.5" />Driver</h3>
          <div className="space-y-2 text-sm">
            <div><span className="text-gray-500">Name:</span> <span className="font-medium">{dn.driver_name || "Not assigned"}</span></div>
            <div><span className="text-gray-500">Phone:</span> <span>{dn.driver_phone || "-"}</span></div>
            <div><span className="text-gray-500">Vehicle:</span> <span>{dn.driver_vehicle || "-"}</span></div>
          </div>
        </CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3 flex items-center gap-1"><FileText className="w-3.5 h-3.5" />Details</h3>
          <div className="space-y-2 text-sm">
            <div><span className="text-gray-500">Created:</span> <span>{formatDate(dn.created_at)}</span></div>
            <div><span className="text-gray-500">Created By:</span> <span>{dn.created_by_name}</span></div>
            <div><span className="text-gray-500">Notes:</span> <span>{dn.notes || "-"}</span></div>
          </div>
        </CardContent></Card>
      </div>

      {/* Line Items */}
      <Card className="border rounded-sm mb-6"><div className="p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Line Items</h3>
        <table data-testid="dn-items-table" className="w-full text-sm">
          <thead><tr className="border-b bg-gray-50/50">
            <th className="p-2.5 text-left text-xs font-semibold uppercase text-gray-500">SKU</th>
            <th className="p-2.5 text-left text-xs font-semibold uppercase text-gray-500">Description</th>
            <th className="p-2.5 text-right text-xs font-semibold uppercase text-gray-500">Qty Ordered</th>
            <th className="p-2.5 text-right text-xs font-semibold uppercase text-gray-500">Qty Delivered</th>
          </tr></thead>
          <tbody>{(dn.line_items || []).map((item, i) => (
            <tr key={i} className="border-b border-gray-100">
              <td className="p-2.5 font-mono text-xs">{item.sku || "-"}</td>
              <td className="p-2.5">{item.description}</td>
              <td className="p-2.5 text-right">{item.quantity}</td>
              <td className="p-2.5 text-right font-medium">{item.delivered_qty || 0}</td>
            </tr>
          ))}</tbody>
        </table>
      </div></Card>

      {/* Proof of Delivery */}
      {pod && (
        <Card className="border rounded-sm border-green-200 bg-green-50/30 mb-6"><CardContent className="p-4">
          <h3 className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-1.5"><Check className="w-4 h-4" />Proof of Delivery</h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div><span className="text-gray-500">Received By:</span> <span className="font-medium">{pod.received_by || "-"}</span></div>
            <div><span className="text-gray-500">Delivered At:</span> <span>{formatDate(pod.delivered_at)}</span></div>
            {pod.signature_url && <div><span className="text-gray-500">Signature:</span> <img src={`${process.env.REACT_APP_BACKEND_URL}${pod.signature_url}`} alt="Signature" className="mt-1 max-w-[200px] border rounded" /></div>}
          </div>
          {pod.photo_urls?.length > 0 && (
            <div className="mt-3"><span className="text-xs text-gray-500 font-semibold uppercase">Photos</span>
              <div className="flex gap-2 mt-1">{pod.photo_urls.map((url, i) => <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt={`POD ${i+1}`} className="w-20 h-20 object-cover rounded border" />)}</div>
            </div>
          )}
        </CardContent></Card>
      )}

      {/* Audit Trail */}
      {dn.audit_trail?.length > 0 && (
        <Card className="border rounded-sm"><CardContent className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Activity Log</h3>
          <div className="space-y-2">{dn.audit_trail.map((a, i) => (
            <div key={i} className="flex items-center gap-3 text-xs text-gray-500 border-b border-gray-50 pb-2">
              <span className="text-gray-400 w-32">{formatDate(a.created_at)}</span>
              <span className="font-medium text-gray-700">{a.user_name}</span>
              <span>{a.details}</span>
            </div>
          ))}</div>
        </CardContent></Card>
      )}

      {/* Driver Assignment Dialog */}
      <Dialog open={driverDialog} onOpenChange={setDriverDialog}>
        <DialogContent><DialogHeader><DialogTitle>Assign Driver</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Driver Name</label>
              <Input data-testid="dialog-driver-name" value={driverForm.driver_name} onChange={(e) => setDriverForm({...driverForm, driver_name: e.target.value})} /></div>
            <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Phone</label>
              <Input data-testid="dialog-driver-phone" value={driverForm.driver_phone} onChange={(e) => setDriverForm({...driverForm, driver_phone: e.target.value})} /></div>
            <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Vehicle</label>
              <Input data-testid="dialog-driver-vehicle" value={driverForm.driver_vehicle} onChange={(e) => setDriverForm({...driverForm, driver_vehicle: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDriverDialog(false)}>Cancel</Button>
            <Button data-testid="confirm-assign-driver-btn" onClick={assignDriver} disabled={actioning || !driverForm.driver_name} className="bg-[#E63946] hover:bg-[#C1121F] text-white">
              {actioning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
        <DialogContent><DialogHeader><DialogTitle>Change Status</DialogTitle></DialogHeader>
          <Select value={newStatus} onValueChange={setNewStatus}>
            <SelectTrigger data-testid="new-status-select"><SelectValue placeholder="Select new status..." /></SelectTrigger>
            <SelectContent>{allowed.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(false)}>Cancel</Button>
            <Button data-testid="confirm-status-change-btn" onClick={changeStatus} disabled={actioning || !newStatus} className="bg-[#E63946] hover:bg-[#C1121F] text-white">
              {actioning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* POD Dialog */}
      <Dialog open={podDialog} onOpenChange={setPodDialog}>
        <DialogContent><DialogHeader><DialogTitle>Capture Proof of Delivery</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Received By (Name)</label>
              <Input data-testid="pod-received-by" value={podForm.received_by} onChange={(e) => setPodForm({...podForm, received_by: e.target.value})} placeholder="Name of person receiving..." /></div>
            <div className="text-xs text-gray-500">Signature and photo uploads can be added after the delivery is confirmed.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPodDialog(false)}>Cancel</Button>
            <Button data-testid="confirm-pod-btn" onClick={submitPod} disabled={actioning || !podForm.received_by} className="bg-green-600 hover:bg-green-700 text-white">
              {actioning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}Confirm Delivery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
